import services from "data"
import folderService from "data/folder"
import rentrollService from "data/rent-roll"
import folderPermissionService from "data/folder-permission"
import { ImmutableBehaviorSubject as BehaviorSubject } from "data/lib/immutable-behavior-subject"
import { local } from "data/lib/local-json"
import { logEvent } from "data/lib/log-event"
import notificationService from "data/notification"
import Client from "data/_client"
import _ from "lodash"
import propertyService from "./property"
import { progress } from "components/notification/subscribe"

// private module logic //

interface ImportObj {
  filename?: string,
  fileStorageId: number|null,
  loading: boolean,
  showImport: boolean,
  validateConfirmVisible: boolean,
  validateResponse: string|null,
  tables?: string[],
  info?: any
}

const initialState = {
  fileStorageId: null,
  loading: false,
  showImport: false,
  validateConfirmVisible: false,
  validateResponse: null
}

const host = process.env.IMPORT_API;
const importStream = new BehaviorSubject<ImportObj>(initialState);

const info = () => ({info: importStream.getValue().info})
function uploadFiles(file:any, is3rdPartyDCF:boolean=false):void {
  if (file.name.endsWith('.mdb')){
    notificationService.notify({
      type: "danger",
      message: "Microsoft Access files are not supported",
      dismiss: 5000
    })
    return
  }
  const userid = _.get(local('auth.profile'), 'user_id')
  const folderid = _.last(local('folderPath'))
  folderPermissionService.getFolderUserPermissions(folderid, userid)
    .then((permissions) => {
      if(permissions['owner']) {
        importStream.next({
          fileStorageId: null,
          loading: false,
          showImport: true,
          validateConfirmVisible: false,
          validateResponse: null,
          ...info()
        });
        const folder    = _.last(JSON.parse(localStorage.folderPath));
        const isFolder  = window.location.pathname.startsWith("/n/folder");
        const isAsset  = window.location.pathname.includes("/asset-management");
        const currentId = isFolder ? folder : isAsset ? _.get(info(), 'info.chartid') : localStorage.getItem('property');
        const headers = {
          'Content-Type': 'multipart/form-data'
        }

        let data = new FormData();

        if (file.name.endsWith('.mdb') && isFolder){
          return
          data.append('attachment', new Blob([file], { type: 'application/msaccess'}), file.name);
        } else {
          data.append('attachment', new Blob([file], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'}), file.name);
        }

        Client.post('upload', data, {headers, host}).then((res)=>{
          if (res.data[0]){
            file.name.endsWith('.mdb') ? importMdbFile(res.data[0], currentId, file.name) : is3rdPartyDCF ? aeImport(res.data[0], folder, file.name) : validateFile(res.data[0], currentId, isFolder, file.name, isAsset)
          } else {
            console.warn("File not uploaded");
            cancelImport();
          }
        }).catch((error)=>{
          notificationService.notify({
            type: "warn",
            message: "The template could not be uploaded successfully at this time",
            dismiss: 5000
          })
          importStream.next({...importStream.getValue(), loading: false})
        });
      } else {
        notificationService.notify({
          type: "success",
          message: "Only folder owners can import data",
          dismiss: 3000
        })
      }
    })
}

function aeImport(responseId:number, folder:number, filename: string) {
  const userid = _.get(local('auth.profile'), 'user_id')
  const importRoute = `import_ae?ids=${responseId}&folderid=${folder}&userid=${userid}`;
  const headers = {
    'Content-Type': 'multipart/form-data; boundary=----WebKitFormBoundarymad6L4uPWejK4h0H'
  }

  const component = progress('user', (event, update, unsubscribe, ctx) => {
    if (event.msg.importjobid != responseId) return
    const progress = event.msg.progress
    if(progress == 100) {
      unsubscribe()
      notificationService.notify({ type: "success", message: `AE Import of ${filename} completed.`, dismiss: 3000 })
    }
    if (event.msg.error) {
      unsubscribe()
      notificationService.notify({ type: "danger", message: `AE Import: ${filename} failed.`, dismiss: 3000 })
    }
    update(progress)
  })
  notificationService.enqueue({ message: `AE Import: ${filename}`, correlationid: `${folder}-${filename}`, component })
  notificationService.notify({ type: "info", message: `AE Import of ${filename} started. Check status in notifications.`, dismiss: 5000 })

  const source = {filename}
  const destination = {folderid: folder}
  let correlationid
  logEvent('import-ae', {source, destination})
  .then((cid) => correlationid = cid)
  .then(() => Client.post(importRoute, null, {headers: {...headers, correlationid}, host}))
  .catch((error)=>{
    notificationService.notify({
      type: "warn",
      message: "The template could not be uploaded successfully at this time",
      dismiss: 5000
    })
    importStream.next({...importStream.getValue(), loading: false})
  })
}

function importMdbFile(fileStorageId:number, currentId:number, filename:string):void {
  const userid = _.get(local('auth.profile'), 'user_id')
  const folderid = _.last(local('folderPath'))
  folderPermissionService.getFolderUserPermissions(folderid, userid)
    .then((permissions) => {
      if(permissions['owner']) {
        const importRoute = `import_access?ids=${fileStorageId}&folderid=${currentId}`;
        const headers = { 'Content-Type': 'multipart/form-data' }

        const source = {filename}
        const destination = {folderid: currentId}
        logEvent('import_dcf-folder', {source, destination})
        .then((correlationid) => Client.post(importRoute, null, {headers:{correlationid, ...headers}, host}))
        .then((res)=>{
          if (res.data){
            notificationService.notify({ type: "info", message: "The database file was imported successfully", dismiss: 5000 });
            if (window.location.href.includes('property-list')){
              services.property.fetchByFolderId(currentId);
            }
            cancelImport();
          } else {
            notificationService.notify({ type: "warn", message: "The database file could not be uploaded successfully at this time", dismiss: 5000 });
            cancelImport();
          }
        });
      } else {
        notificationService.notify({
          type: "warn",
          message: "Only folder owners can import data",
          dismiss: 3000
        })
      }
    })
}

function importVTSFolder(folderId:number, route:string){
  Client.post(route, null, {host}).then((res)=>{
    if (res?.data?.status === "Success"){
      notificationService.notify({
        type: "info",
        message: `${res.data.imported_deal_count} deals imported successfully`,
        dismiss: 3000
      })
      folderService.fetchAllFolders()
      propertyService.fetchByFolderId(folderId)
    } else {
      notificationService.notify({
        type: "warn",
        message: res?.data?.error || "VTS credentials have not been installed for this Organization.",
        dismiss: 3000
      })
    }
  })
}

function validateFile(fileStorageId:number, currentId:number, isFolder:boolean, filename:string, isAsset: boolean):void {
  const validateRoute =
    isFolder ? `validate?ids=${fileStorageId}&folderid=${currentId}`
    : isAsset ? `validate?ids=${fileStorageId}&chartid=${currentId}`
    : `validate?ids=${fileStorageId}&propertyid=${currentId}`

  const headers = {
    'Content-Type': 'multipart/form-data'
  }

  Client.get(validateRoute, {headers, host}).then((res)=>{
    let validated = res.data[0];

    if (validated.information){
      importStream.next({
        filename,
        fileStorageId,
        loading: false,
        showImport: true,
        validateConfirmVisible: true,
        validateResponse: validated.information,
        ...info()
      });
    } else if (validated.data_compatible && validated.format_compatible){
      importStream.next({
        filename,
        fileStorageId,
        loading: false,
        showImport: true,
        validateConfirmVisible: true,
        validateResponse: `Inserting ${validated.inserted_row} row(s). Updating ${validated.updated_row} row(s). ${validated.unchanged_row} row(s) unchanged.`,
        tables: _.uniq(_.filter(_.map(_.get(res, 'data.0.rows'), x => x.table || x.item_type && 'lineitems'))),
        ...info()
      });
    } else if (validated.data_compatible === false){
      notificationService.notify({ type: "warn", message: "Invalid data provided. Please review your template and try again", dismiss: 5000 });
      cancelImport();
    } else {
      notificationService.notify({ type: "warn", message: `${validated.error_mess || ''} Please review your template and try again`, dismiss: 5000});
      cancelImport();
    }
  });
}

function importCb():void {
  const userid = _.get(local('auth.profile'), 'user_id')
  const folderid = _.last(local('folderPath'))
  importStream.next({...importStream.getValue(), loading: true})
  folderPermissionService.getFolderUserPermissions(folderid, userid)
    .then((permissions) => {
      if(permissions['owner']) {
        const folder            = _.last(JSON.parse(localStorage.folderPath));
        const chartid = _.get(info(), 'info.chartid')
        const isFolder          = window.location.pathname.startsWith("/n/folder");
        const isAsset  = window.location.pathname.includes("/asset-management");
        const currentPropertyId = local('property');
        const currentId         = isFolder ? folder : isAsset ? chartid : currentPropertyId;
        const { fileStorageId, filename, tables } = importService.value()
        const importRoute =
          isFolder ?  `import?ids=${fileStorageId}&folderid=${currentId}`
          : isAsset ? `import?ids=${fileStorageId}&chartid=${currentId}`
          :           `import?ids=${fileStorageId}&propertyid=${currentId}`;

        const headers = {
          'Content-Type': 'multipart/form-data'
        }

        const source = { filename }
        const destination = {
          [isFolder ? 'folderid' : isAsset ? 'chart' : 'propertyid']: currentId,
        }
        return logEvent(`import-${isFolder ? 'folder' : isAsset ? 'chart' : 'property'}`, {source, destination})
        .then((correlationid) => Client.post(importRoute, null, {headers:{...headers, correlationid}, host}))
        .then((res)=>{
          notificationService.notify({ type: "info", message: "The template was imported successfully", dismiss: 5000});
          _.forEach(tables, (table) => {
            try {
              services[table].refresh()
            } catch(e) {
              console.log(e)
            }
          })
          cancelImport();
        })
        .catch((err: {message: string}) => {
          notificationService.notify({ type: "warn", message: "The template could not be imported successfully at this time", dismiss: 5000 })
          cancelImport();
          return err
        })
      } else {
        notificationService.notify({
          type: "warn",
          message: "Only folder owners can import data",
          dismiss: 3000
        })
      }
    })
}

async function importPDF(file:any): Promise<void> {
  const currentPropertyId = local('property')
  const userid = _.get(local('auth.profile'), 'user_id')
  const folderid = _.last(local('folderPath'))
  const rentRolls = await rentrollService.fetchRentRoll(currentPropertyId)

  if(_.get(rentRolls, "length") > 0) {
    notificationService.notify({
      type: "danger",
      message: "The PDF rent roll import currently only supports import into empty rent rolls",
      dismiss: 3000
    })
    return
  }
  importStream.next({...importStream.getValue(), loading: true})
  folderPermissionService.getFolderUserPermissions(folderid, userid)
    .then((permissions) => {
      if(permissions['owner']) {
        const headers = {
          'Content-Type': 'multipart/form-data'
        }

        let data = new FormData()
        data.append('file', new Blob([file], { type: 'application/pdf'}), file.name)

        Client.post("pdf/rentroll", data, {headers})
          .then(res => {
            let payload = {
              propertyId: currentPropertyId,
              folderId: folderid,
              jobId: res.jobid,
              _synchronous: false
            }
            Client.post("msg/svc_import_pdf_rentroll", payload)
              .then(() => {
                importStream.next({...importStream.getValue(), loading: false})
              }).catch(res => {
                notificationService.notify({
                  type: "danger",
                  message: _.get(res, "error.text"),
                  dismiss: 3000
                })
                importStream.next({...importStream.getValue(), loading: false})
              })
          }).catch(error => {
            notificationService.notify({
              type: "danger",
              message: error.message,
              dismiss: 3000
            })
            importStream.next({...importStream.getValue(), loading: false})
          })
      } else {
        notificationService.notify({
          type: "warn",
          message: "Only folder owners can import data",
          dismiss: 3000
        })
        importStream.next({...importStream.getValue(), loading: false})
      }
    }).catch(error => {
      notificationService.notify({
        type: "danger",
        message: error.message,
        dismiss: 3000
      })
      importStream.next({...importStream.getValue(), loading: false})
    })
}

function cancelImport():void {
  importStream.next({...initialState});
}

function showImport(show: boolean, info: any):void {
  importStream.next({...importService.value(), showImport: show, info});
}

// public module interface //

const importService = {
  stream: () => importStream.asObservable(),
  value: () => importStream.getValue(),
  importCb: () => importCb(),
  cancelImport: () => cancelImport(),
  importVTSFolder: (folderId:number, route:string) => importVTSFolder(folderId, route),
  showImport: (show: boolean, info?: any) => showImport(show, info),
  uploadFiles: (files:any, is3rdPartyDCF:boolean) => uploadFiles(files, is3rdPartyDCF),
  importPDF
}

export default importService
